import { motion } from "framer-motion";
import { IMAGES } from "@/data/content";
import { Eyebrow, Reveal } from "./Reveal";

const HIGHLIGHTS = [
  {
    num: "01",
    title: "Cafés d'origine",
    desc: "Des grains tracés, torréfiés en douce par des artisans indépendants.",
  },
  {
    num: "02",
    title: "Beurre de baratte breton",
    desc: "Un feuilletage AOP, levé chaque nuit dans notre fournil.",
  },
  {
    num: "03",
    title: "Cuisine du marché",
    desc: "Une carte courte qui suit le rythme des halles et des saisons.",
  },
];

export const About = () => (
  <section
    id="le-cafe"
    data-testid="about-section"
    className="relative overflow-hidden bg-[#FAF7F2] py-24 lg:py-36"
  >
    <span
      aria-hidden="true"
      className="text-outline-cream pointer-events-none absolute -top-6 right-0 hidden select-none font-display text-[11rem] font-semibold leading-none lg:block"
      style={{ WebkitTextStroke: "1px rgba(27,59,43,0.08)" }}
    >
      Arcades
    </span>

    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:gap-12">
      <div className="relative lg:col-span-5">
        <Reveal>
          <div className="relative overflow-hidden rounded-[28px] shadow-[0_24px_70px_rgba(27,59,43,0.14)]">
            <img
              src={IMAGES.interior}
              alt="Salle du café, murs vert forêt et tasse de crème"
              loading="lazy"
              className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105"
            />
          </div>
        </Reveal>
        <motion.div
          initial={{ opacity: 0, y: 40, rotate: -4 }}
          whileInView={{ opacity: 1, y: 0, rotate: -3 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="absolute -bottom-10 -right-3 w-44 overflow-hidden rounded-2xl border-[6px] border-[#FAF7F2] shadow-[0_20px_50px_rgba(27,59,43,0.2)] sm:-right-8 sm:w-56"
        >
          <img
            src={IMAGES.terrace}
            alt="Terrasse du café, chaises de bistrot"
            loading="lazy"
            className="aspect-[3/4] w-full object-cover"
          />
        </motion.div>
        <span
          aria-hidden="true"
          className="absolute -left-10 -top-10 -z-10 h-44 w-44 rounded-full border border-[#C85A32]/30"
        />
      </div>

      <div className="lg:col-span-6 lg:col-start-7">
        <Reveal>
          <Eyebrow>L'esprit des Arcades</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <h2
            data-testid="about-title"
            className="mt-6 font-display text-3xl font-semibold tracking-tight text-[#1B3B2B] sm:text-4xl lg:text-5xl"
          >
            Un café de quartier,
            <br />
            l'exigence d'une <em className="italic text-[#C85A32]">maison</em>.
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-[#3D5A4B] sm:text-lg">
            Installé depuis 2018 sous les arcades de la place des Lices, notre
            café est né d'une conviction simple : les bonnes choses demandent
            du temps. Du temps pour torréfier, pour feuilleter, pour mijoter —
            et surtout pour savourer.
          </p>
          <p className="mt-5 max-w-xl text-base font-light leading-relaxed text-[#3D5A4B] sm:text-lg">
            Tout est fait maison, du kouign-amann du matin à la soupe du midi.
            Nous travaillons main dans la main avec des producteurs bretons et
            des torréfacteurs indépendants, au gré du marché.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-8 sm:grid-cols-3">
          {HIGHLIGHTS.map((h, i) => (
            <Reveal key={h.num} delay={0.1 + i * 0.1}>
              <div data-testid={`about-highlight-${i + 1}`} className="border-t border-[#1B3B2B]/15 pt-5">
                <span className="font-mono text-xs tracking-[0.2em] text-[#C85A32]">{h.num}</span>
                <h3 className="mt-3 font-display text-lg font-semibold text-[#1B3B2B]">{h.title}</h3>
                <p className="mt-2 text-sm font-light leading-relaxed text-[#3D5A4B]/90">{h.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.35}>
          <p className="mt-12 font-display text-xl italic text-[#1B3B2B]/70">
            — Élise &amp; Marc, fondateurs
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);
